import { useLocalSearchParams, useRouter } from 'expo-router';
import { X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { api } from '~/api';
import { writeTripsCache } from '~/lib/tripsCache';
import { useMember } from '~/stores/useMember';
import { bookingReference } from '~/lib/bookingDisplay';
import { BookingDetailsCard } from '~/components/BookingDetailsCard';
import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { Button, LoadingState } from '~/components/ui';
import { colors, fontFamily, radii, spacing } from '~/ui/tokens';

function SuccessScreen() {
    const router = useRouter();
    const { orderId, partnerOrderId } = useLocalSearchParams<{ orderId: string; partnerOrderId: string }>();
    const userEmail = useMember(state => state.user?.email ?? '');

    const [order, setOrder] = useState<Wire.OrderSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        const targetId = Number(orderId);

        (async () => {
            // A just-created order can lag order/info by a second or two, and
            // that read can hit a transient rate limit — retry before deciding
            // the confirmation is missing, so someone who just booked never
            // sees a false "not found".
            for (let attempt = 0; attempt < 5; attempt += 1) {
                try {
                    const res = await api.order.info();
                    const found = res.data.orders.find(o => targetId > 0 ? o.orderId === targetId : Boolean(partnerOrderId) && o.partnerOrderId === partnerOrderId) ?? null;
                    if (!active) {
                        return;
                    }
                    if (found) {
                        if (userEmail) writeTripsCache(userEmail, res.data.orders);
                        setOrder(found);
                        setLoading(false);
                        return;
                    }
                } catch {
                    if (!active) {
                        return;
                    }
                }
                await new Promise(resolve => setTimeout(resolve, 1500));
            }
            if (active) {
                setLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [orderId, partnerOrderId, userEmail]);

    const bookingRef = order ? bookingReference(order.orderId, order.status) : null;

    return (
        <Screen>
            <TopNav />
            <View style={styles.body}>
                {!order ? <View style={styles.card}>
                    <View style={[styles.iconCircle, styles.iconCircleMuted]}>
                        {loading ? <ActivityIndicator color={colors.white} /> : <X size={28} color={colors.white} />}
                    </View>
                    <Text style={styles.title}>{loading ? 'Checking your booking' : 'Booking status unavailable'}</Text>
                    <Text style={styles.subtitle}>
                        {loading ? 'Checking the latest payment and reservation status.' : 'We couldn’t load this booking. Open Your trips to check its latest status, or try again.'}
                    </Text>
                </View> : null}

                {loading ? (
                    <LoadingState label="Loading your confirmation…" />
                ) : order ? (
                    <BookingDetailsCard order={order} bookingRef={bookingRef} />
                ) : null}

                {!loading && !order ? <Button label="View your trips" onPress={() => router.push('/trips')} style={styles.homeBtn} /> : null}
                <Button label="Return home" onPress={() => router.push('/')} style={styles.homeBtn} />
            </View>
        </Screen>
    );
}

const styles = StyleSheet.create({
    body: { maxWidth: 720, width: '100%', alignSelf: 'center', paddingHorizontal: spacing(3), paddingTop: spacing(3), paddingBottom: spacing(9), gap: spacing(2) },
    card: { backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: spacing(3), alignItems: 'center', gap: spacing(1) },
    iconCircle: { width: 60, height: 60, borderRadius: radii.full, backgroundColor: colors.green, alignItems: 'center', justifyContent: 'center', marginBottom: spacing(0.5) },
    iconCircleMuted: { backgroundColor: colors.inkFaint },
    title: { fontFamily: fontFamily.bold, fontSize: 28, color: colors.ink, textAlign: 'center' },
    subtitle: { fontFamily: fontFamily.regular, fontSize: 15, lineHeight: 22, color: colors.inkSoft, textAlign: 'center', maxWidth: 460 },
    idRow: { flexDirection: 'row', gap: spacing(1), alignItems: 'baseline', marginTop: spacing(1) },
    idLabel: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
    idValue: { fontFamily: fontFamily.bold, fontSize: 18, color: colors.ink },
    homeBtn: { alignSelf: 'flex-start', borderWidth: 1, borderColor: colors.line, paddingVertical: spacing(1.25), paddingHorizontal: spacing(3), borderRadius: radii.md },
});

export default SuccessScreen;
